/**
 * Coach program library + delete/toggle. Ported from the original app's
 * ProgramsList.tsx + page.tsx wrapper — same UI verbatim, with next/link → react-router
 * Link, the api client, and PageHeader. Reads GET /api/programs (coach: all programs +
 * counts), PATCH /api/programs/:id (toggle active), DELETE /api/programs/:id.
 *
 * D1/SQLite returns booleans as INTEGER 0/1, so is_active/is_template are numbers.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Dumbbell,
  Users,
  Calendar,
  ChevronRight,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';

interface Program {
  id: string;
  name: string;
  description: string | null;
  duration_weeks: number;
  workouts_per_week: number;
  difficulty: string;
  is_active: number;
  is_template: number;
  workout_count: number;
  assigned_count: number;
  created_at: string;
}

const difficultyColor: Record<string, string> = {
  beginner: 'var(--success)',
  intermediate: 'var(--warning)',
  advanced: 'var(--error)',
};

export default function CoachPrograms() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'template'>('all');

  useEffect(() => {
    api
      .get<{ programs: Program[] }>('/api/programs')
      .then((data) => setPrograms(data.programs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function toggleActive(id: string, currentlyActive: boolean) {
    try {
      await api.patch(`/api/programs/${id}`, { is_active: !currentlyActive });
      setPrograms((prev) => prev.map((p) => (p.id === id ? { ...p, is_active: currentlyActive ? 0 : 1 } : p)));
    } catch { /* */ }
  }

  async function deleteProgram(id: string) {
    if (!confirm('Delete this program? This cannot be undone.')) return;
    try {
      await api.del(`/api/programs/${id}`);
      setPrograms((prev) => prev.filter((p) => p.id !== id));
    } catch { /* */ }
  }

  const filtered = programs.filter((p) => {
    if (filter === 'active') return !!p.is_active;
    if (filter === 'template') return !!p.is_template;
    return true;
  });

  return (
    <>
      <PageHeader
        title="Programs"
        subtitle="Training program library"
        action={
          <Link to="/coach/programs/new" className="btn-gold text-sm py-2 px-4">
            + New
          </Link>
        }
      />
      {loading ? (
        <div className="px-4 flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse h-28" style={{ background: 'var(--surface)' }} />
          ))}
        </div>
      ) : (
        <div className="px-4 flex flex-col gap-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="card text-center py-2">
              <p className="text-lg font-bold text-white">{programs.length}</p>
              <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>Total</p>
            </div>
            <div className="card text-center py-2">
              <p className="text-lg font-bold" style={{ color: 'var(--success)' }}>{programs.filter((p) => p.is_active).length}</p>
              <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>Active</p>
            </div>
            <div className="card text-center py-2">
              <p className="text-lg font-bold" style={{ color: 'var(--info)' }}>{programs.filter((p) => p.is_template).length}</p>
              <p className="text-[10px]" style={{ color: 'var(--gray-400)' }}>Templates</p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex gap-1.5 p-1 rounded-lg" style={{ background: 'var(--surface)' }}>
            {(['all', 'active', 'template'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="flex-1 py-1.5 text-xs font-medium rounded-md capitalize transition-all"
                style={{
                  background: filter === f ? 'var(--gold)' : 'transparent',
                  color: filter === f ? 'var(--navy)' : 'var(--gray-400)',
                }}
              >
                {f}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="card text-center py-8">
              <Dumbbell size={32} style={{ color: 'var(--gold-muted)' }} className="mx-auto mb-2" />
              <p className="text-white font-semibold">No programs yet</p>
              <p className="text-sm mt-1" style={{ color: 'var(--gray-400)' }}>
                Create your first training program
              </p>
            </div>
          ) : (
            filtered.map((program) => (
              <div key={program.id} className="card">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-semibold">{program.name}</p>
                      {!program.is_active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(107,114,128,0.2)', color: 'var(--gray-400)' }}>
                          Inactive
                        </span>
                      )}
                      {!!program.is_template && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(59,130,246,0.12)', color: 'var(--info)' }}>
                          Template
                        </span>
                      )}
                    </div>
                    {program.description && (
                      <p className="text-xs line-clamp-2" style={{ color: 'var(--gray-400)' }}>{program.description}</p>
                    )}
                  </div>
                  <Link to={`/coach/programs/${program.id}`}>
                    <ChevronRight size={18} style={{ color: 'var(--gray-500)' }} />
                  </Link>
                </div>

                <div className="flex items-center gap-4 mt-3 text-xs" style={{ color: 'var(--gray-500)' }}>
                  <span
                    className="px-2 py-0.5 rounded-full capitalize"
                    style={{ background: 'rgba(255,255,255,0.06)', color: difficultyColor[program.difficulty] || 'var(--gray-400)' }}
                  >
                    {program.difficulty}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar size={10} /> {program.duration_weeks}wk &middot; {program.workouts_per_week}x/wk
                  </span>
                  <span className="flex items-center gap-1">
                    <Dumbbell size={10} /> {program.workout_count} workouts
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={10} /> {program.assigned_count}
                  </span>
                </div>

                <div className="flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <button
                    onClick={() => toggleActive(program.id, !!program.is_active)}
                    className="flex items-center gap-1 text-xs"
                    style={{ color: program.is_active ? 'var(--success)' : 'var(--gray-400)' }}
                  >
                    {program.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                    {program.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => deleteProgram(program.id)}
                    className="flex items-center gap-1 text-xs ml-auto"
                    style={{ color: 'var(--error)' }}
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}
